import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login } from '../lib/api';
import { setAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle } from 'lucide-react';

export default function OrgLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const returnUrl = searchParams.get('returnUrl') || '/org/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(email, password);
      const { access_token, user } = response.data;
      
      if (user.role !== 'org_user' && user.role !== 'admin') {
        setError('Organization access required');
        setLoading(false);
        return;
      }

      setAuth(access_token, user);
      navigate(returnUrl);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="org-login-page">
      <div className="login-form-side bg-white">
        <div className="login-box">
          <div className="mb-12">
            <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              AI Accountability Standards
            </h1>
            <p className="text-zinc-500 mt-2">Organization Portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="alert alert-error flex items-center gap-2" data-testid="login-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="form-field">
              <Label htmlFor="email" className="form-label">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input rounded-none"
                placeholder="user@organization.com"
                required
                data-testid="email-input"
              />
            </div>

            <div className="form-field">
              <Label htmlFor="password" className="form-label">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input rounded-none"
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary rounded-none h-11"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200">
            <p className="text-sm text-zinc-500 text-center">
              Don't have an account? Contact your organization administrator.
            </p>
          </div>
        </div>
      </div>
      <div className="login-image-side bg-zinc-100 flex items-center justify-center">
        <div className="text-center p-12">
          <div className="text-zinc-300 text-9xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            ORG
          </div>
          <p className="text-zinc-400 mt-4 text-sm tracking-widest uppercase">
            Member Access
          </p>
        </div>
      </div>
    </div>
  );
}
